<template>
  <div class="locations" :class="cssClass">
    <h3
        class="margin-0"
        :class="{
    'text-danger': store.error && store.requireFields?.includes('locations'),
  }"
    >
      <span class="locations-title">
        {{ t('measurement-location').toUpperCase() }}
      </span>

      <div
          v-if="cssClass !== 'vms' && locationsWithCoords.length"
          class="select-all-container"
      >
        <span class="select-all-text">
          {{ allSelected ? t('unmark') : t('mark') }} {{ t('all').toLowerCase() }}
        </span>

        <input-checkbox
            class="select-all-checkbox"
            field-name="selectAll"
            field-value=""
            :checkedCondition="allSelected"
            @clicked="setAllFilter()"
        />
      </div>
    </h3>

    <div class="map-wrapper">
      <!-- ZAWSZE istnieje -->
      <div ref="mapContainer" class="map-container"></div>

      <!-- Tylko info o braku danych -->
      <p
          v-if="!locationsWithCoords.length"
          class="no-data center-aligned"
      >
        {{ t('no-data') }}
      </p>
    </div>
  </div>
</template>

<script>
import InputCheckbox from "../InputCheckbox.vue";
import { useUserStore } from "@/stores/user.store";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import {
  MAP_TILES_ATTRIBUTION,
  MAP_TILES_MAX_ZOOM,
  MAP_TILES_MIN_ZOOM,
  MAP_TILES_NO_WRAP,
  MAP_TILES_SUBDOMAINS,
  MAP_TILES_TMS,
  MAP_TILES_URL_TEMPLATE,
} from "@/config/mapTiles";

// 🔧 PATCH: zabezpieczenie _updatePosition tooltipa/popupu
if (!L.__safeOverlayPatched) {
  const origTooltipUpdate = L.Tooltip.prototype._updatePosition;
  L.Tooltip.prototype._updatePosition = function (...args) {
    if (!this._map) return;               // <-- kluczowa linia
    return origTooltipUpdate.apply(this, args);
  };

  const origPopupUpdate = L.Popup.prototype._updatePosition;
  L.Popup.prototype._updatePosition = function (...args) {
    if (!this._map) return;
    return origPopupUpdate.apply(this, args);
  };

  L.__safeOverlayPatched = true;
}

export default {
  name: "LocationList2",
  components: { InputCheckbox },
  setup() {
    // Status (kolor markera: aktywny/nieaktywny) widoczny tylko dla administratorów
    // (klucz `LocationStatuses` → ROLE_7). Dla pozostałych użytkowników markery są jednolite.
    return { userStore: useUserStore() };
  },
  props: {
    store: { required: true },
    cssClass: String,
    freshnessThresholdMinutes: { type: Number, default: 30 },
    lastTimeIsUnixSeconds: { type: Boolean, default: false },
    // co jaki czas przeliczać statusy widoczności (ms)
    statusTickIntervalMs: { type: Number, default: 5 * 60 * 1000 },
  },
  data() {
    return {
      map: null,
      clusterGroup: null,
      markersById: {},
      locById: {},
      now: Date.now(),
      timer: null,
    };
  },
  created() {
    this.store.getLocations?.();
  },
  mounted() {
    this.initMap();
    this.renderMarkers();

    // co 5 minut odświeżamy "aktywność" stacji
    this.timer = setInterval(() => {
      this.now = Date.now();
    }, this.statusTickIntervalMs);

    // bardzo ważne: po wyrenderowaniu wymuś przeliczenie rozmiaru mapy
    this.$nextTick(() => {
      if (this.map) this.map.invalidateSize();
    });

    // przy zmianie rozmiaru okna też koryguj
    window.addEventListener("resize", this.onResize);
  },
  beforeUnmount() {
    if (this.timer) clearInterval(this.timer);
    window.removeEventListener("resize", this.onResize);

    if (this.clusterGroup) {
      this.clusterGroup.clearLayers();
      this.clusterGroup = null;
    }

    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  },
  computed: {
    locationsWithCoords() {
      const arr = Array.isArray(this.store.locations) ? this.store.locations : [];
      return arr.filter((loc) => {
        const lat = parseFloat(loc.geoLat ?? loc.geo_lat);
        const lng = parseFloat(loc.geoLong ?? loc.geo_long);
        return Number.isFinite(lat) && Number.isFinite(lng);
      });
    },
    selectedIdSet() {
      const ids = this.store?.filters?.locations || [];
      return new Set(ids.map((x) => Number(x)));
    },
    allSelected() {
      const locs = this.locationsWithCoords;
      if (!locs.length) return false;
      return locs.every((l) => this.selectedIdSet.has(Number(l.id)));
    },
    thresholdMs() {
      return this.freshnessThresholdMinutes * 60 * 1000;
    },
    canSeeStatus() {
      return this.userStore.hasAccess('LocationStatuses');
    },
  },
  watch: {
    // nie potrzebujemy deep – computed i tak zmieni referencję
    locationsWithCoords() {
      this.renderMarkers();
    },
    "store.filters.locations"() {
      this.updateMarkersVisual();
    },
    now() {
      this.updateMarkersVisual();
    },
  },
  methods: {
    onResize() {
      if (this.map) {
        this.map.invalidateSize();
      }
    },

    /** ------------- MAPA ------------- **/
    initMap() {
      if (this.map || !this.$refs.mapContainer) return;

      const polandBounds = [
        [49.0, 14.0],
        [55.0, 24.5],
      ];

      this.map = L.map(this.$refs.mapContainer, {
        center: [52.0, 19.0],
        zoom: 6,
        minZoom: 5,
        maxZoom: 16,
        maxBounds: polandBounds,
        maxBoundsViscosity: 1.0,
        zoomAnimation: false,
        markerZoomAnimation: false,
        fadeAnimation: false,
        zoomDelta: 1.5,
        zoomSnap: 1,
      });

      L.tileLayer(MAP_TILES_URL_TEMPLATE, {
        minZoom: MAP_TILES_MIN_ZOOM,
        maxZoom: MAP_TILES_MAX_ZOOM,
        attribution: MAP_TILES_ATTRIBUTION,
        tms: MAP_TILES_TMS,
        noWrap: MAP_TILES_NO_WRAP,
        subdomains: MAP_TILES_SUBDOMAINS,
      }).addTo(this.map);

      this.clusterGroup = L.markerClusterGroup({
        showCoverageOnHover: false,
        maxClusterRadius: 40,
        spiderfyOnEveryZoom: false,
        spiderfyOnMaxZoom: true,
        disableClusteringAtZoom: 12,
        animate: false, // ⬅️ ważne przy circleMarker
      });

      // własna obsługa kliknięcia klastra – tylko spiderfy
      this.clusterGroup.on("clusterclick", (e) => {
        e.layer.spiderfy();
        if (e.originalEvent) {
          L.DomEvent.preventDefault(e.originalEvent);
          L.DomEvent.stopPropagation(e.originalEvent);
        }
      });

      this.map.addLayer(this.clusterGroup);

      L.Marker.prototype.options.icon = L.icon({
        iconRetinaUrl: markerIcon2x,
        iconUrl: markerIcon,
        shadowUrl: markerShadow,
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        tooltipAnchor: [16, -28],
        shadowSize: [41, 41],
      });
    },

    renderMarkers() {
      if (!this.map || !this.clusterGroup) return;

      this.clusterGroup.clearLayers();
      this.markersById = {};
      this.locById = {};

      const bounds = [];

      this.locationsWithCoords.forEach((loc) => {
        const lat = parseFloat(loc.geoLat ?? loc.geo_lat);
        const lng = parseFloat(loc.geoLong ?? loc.geo_long);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

        this.locById[loc.id] = loc;
        const html = this.getLocationHtml(loc);

        const marker = L.marker([lat, lng], {
          icon: this.createMarkerIcon(loc),
        }).on("click", () => this.handleMarkerClick(loc.id));

        marker.bindTooltip(html, {
          direction: "top",
          offset: [0, -6],
          opacity: 0.9,
          permanent: false,
          sticky: true,
          className: "location-tooltip",
        });

        this.clusterGroup.addLayer(marker);
        this.markersById[loc.id] = marker;
        bounds.push([lat, lng]);
      });

      if (!bounds.length) return;

      if (bounds.length === 1) {
        this.map.setView(bounds[0], 13);
      } else {
        this.map.fitBounds(bounds, {
          padding: [40, 40],
          maxZoom: 11,
        });
      }
    },

    updateMarkersVisual() {
      if (!this.markersById) return;

      Object.entries(this.markersById).forEach(([id, marker]) => {
        const loc = this.locById[id];
        if (!loc || !marker.setIcon) return;
        marker.setIcon(this.createMarkerIcon(loc));
      });
    },

    createMarkerIcon(loc) {
      const selected = this.isChecked(loc.id);

      const classes = ["map-marker"];
      // Klasę "inactive" (czerwony marker = stacja offline) ustawiamy tylko dla administratorów.
      // Dla pozostałych użytkowników wszystkie markery wyglądają jednakowo — status pozostaje ukryty.
      if (this.canSeeStatus && !this.isActive(loc)) classes.push("map-marker--inactive");
      if (selected) classes.push("map-marker--selected");

      return L.divIcon({
        className: classes.join(" "),
        html: "",          // sam wygląd zrobimy w CSS
        iconSize: selected ? [18, 18] : [14, 14],
        iconAnchor: selected ? [9, 9] : [7, 7], // środek kropki
      });
    },
    // getMarkerStyle(loc) {
    //   const selected = this.isChecked(loc.id);
    //   const active = this.isActive(loc);
    //
    //   let color = active ? "#008000" : "#dc1011";
    //   let radius = selected ? 10 : 7;
    //
    //   return {
    //     color,
    //     fillColor: color,
    //     radius,
    //     weight: selected ? 2 : 1,
    //     fillOpacity: 0.9,
    //   };
    // },

    handleMarkerClick(id) {
      this.setFilter(id);
    },

    /** ------------- LOGIKA WYBORU ------------- **/
    setFilter(id) {
      this.store.addItem?.("LocationList", Number(id));
    },
    isChecked(id) {
      return this.selectedIdSet.has(Number(id));
    },
    setAllFilter() {
      this.store.checkAllLocations?.();
    },

    /** ------------- AKTYWNOŚĆ STACJI ------------- **/
    parseTimestamp(value) {
      if (value == null) return NaN;
      if (typeof value === "number") {
        return this.lastTimeIsUnixSeconds ? value * 1000 : value;
      }
      const trimmed = String(value).trim();
      if (/^\d+$/.test(trimmed)) {
        const n = Number(trimmed);
        return this.lastTimeIsUnixSeconds ? n * 1000 : n;
      }
      const ms = Date.parse(trimmed);
      return Number.isNaN(ms) ? NaN : ms;
    },
    isActive(loc) {
      const ts = this.parseTimestamp(loc?.lastVehicleTime);
      if (Number.isNaN(ts)) return false;
      return this.now - ts <= this.thresholdMs;
    },

    getLocationHtml(loc) {
      const name = loc.shortName ?? `ID ${loc.id}`;
      const roadLabel = this.t ? this.t("road-no") : "Road";
      const chainageLabel = this.t ? this.t("chainage") : "Chainage";

      return `
        <strong>${name}</strong><br/>
        ${roadLabel}: ${loc.roadNumber ?? ""}<br/>
        ${chainageLabel}: ${loc.chainage ?? ""}
      `;
    },
  },
};
</script>

<style scoped>
.locations h3 {
  padding: 0.85rem 1rem 0.9rem 1rem;
  flex: 0 0 445px;
  width: 445px;
  border-right: 1px solid #bfbfbf;
  border-bottom: 1px solid #bfbfbf;
  border-top: 1px solid #bfbfbf;
  display: flex;
  align-items: center;
}

.locations-title {
  margin-right: 1rem;
}

.map-wrapper {
  border-right: 1px solid #bfbfbf;
  border-bottom: 1px solid #bfbfbf;
}

.map-container {
  width: 100%;
  height: 360px;
}

.no-data {
  padding: 1rem;
}

.select-all-container {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 0;
  padding-right: 9px;
}

.select-all-text {
  font-size: 12px;
  margin-right: 0.5rem;
}

.select-all-container :deep(.select-all-checkbox.container),
.select-all-container :deep(.select-all-checkbox.container2) {
  width: 22px;
  height: 22px;
  min-width: 22px;
  min-height: 22px;
  margin: 0;
  transform: none;
}

:deep(.map-marker) {
  background-color: #008000;    /* aktywny = zielony */
  border-radius: 50%;
  border: 2px solid #ffffff;
  width: 12px;
  height: 12px;
  box-shadow: 0 0 2px rgba(0, 0, 0, 0.6);
  cursor: pointer;
}

/* nieaktywny punkt */
:deep(.map-marker--inactive) {
  background-color: #dc1011;    /* czerwony */
}

/* zaznaczony punkt – większy i z mocniejszym cieniem */
:deep(.map-marker--selected) {
  background-color: #ffb703;
  border-color: #1d3557;
  box-shadow: 0 0 0 3px rgba(255, 183, 3, 0.35), 0 0 8px rgba(0, 0, 0, 0.85);
}

/* usunięcie brzydkiej niebieskiej obwódki (focus outline) */
:deep(.leaflet-container),
:deep(.leaflet-container *:focus) {
  outline: none !important;
}
</style>
